import { api } from '@verifime/api-definition';
import { reportPageActionToNewRelic } from '@verifime/utils';
import { useEffect, useState } from 'react';
import { failureReason } from '../../WorkflowMetadataContext/workflowMetadataReporting';
import { readJoinedInvites, type TJoinedInvites } from '../joinedInvites';

/**
 * The New Relic page action for a failed read. The browser agent does not collect console output in
 * this deployment, so this is what engineering sees. It carries `source` (a constant label) and
 * `status` (a reason string): never an id, a token, the error object or anything from a response.
 */
export const JOINABLE_INVITES_READ_FAILED_PAGE_ACTION = 'joinable-invites-read-failed';

/** The joinable-invites endpoint answers 409 when there is nothing to combine for the invite. */
const HTTP_CONFLICT = 409;

/**
 * What the joinable-invites read came to, discriminated by a string (`status`) so it narrows in
 * apps that compile this source without strictNullChecks.
 *
 * - `loading`: the read is running.
 * - `single`: the invite stands alone. Either there is no tracking reference, or the server answered
 *   409 (the invite was not decided AskFirst or was never activated; verifime-adr#572 Q6).
 * - `loaded`: the server listed the invites that join.
 * - `error`: any other failure. The caller shows its error; this never falls back to `single`,
 *   because a plan shown for one invite could be refused by the confirmation.
 */
export type TJoinableInvitesState =
  | { status: 'loading' }
  | { status: 'single' }
  | { status: 'loaded'; joinedInvites: TJoinedInvites }
  | { status: 'error' };

/** The outcome of the read for one tracking reference, kept with the reference it answers. */
type TSettledRead = {
  trackingReference: string;
  state: Exclude<TJoinableInvitesState, { status: 'loading' }>;
};

const isConflict = (error: unknown): boolean =>
  (error as { response?: { status?: unknown } } | null | undefined)?.response?.status ===
  HTTP_CONFLICT;

/**
 * Reads the invites that join the customer's choice for an opened invite
 * (`GET /v1/tracking/{trackingReference}/joinable-invites`). The portal shows what the server
 * lists and never re-derives the join. It is read fresh on every mount: the list changes when the
 * customer activates another invite.
 *
 * It calls the generated client directly rather than through a react-query hook, so a failure is
 * logged here with a reason only (the HTTP status, `network-error` or `invalid-response`) and
 * never as the error object, which carries the request and the response body. The same choice as
 * `useWorkflowMetadataByTrackingReference`.
 */
export function useJoinableInvites(trackingReference: string | null): TJoinableInvitesState {
  const [settled, setSettled] = useState<TSettledRead | undefined>(undefined);

  useEffect(() => {
    if (!trackingReference) {
      setSettled(undefined);
      return;
    }

    // A response for a tracking reference that is no longer current is dropped.
    let isCurrent = true;
    const settle = (state: TSettledRead['state']) => {
      if (isCurrent) {
        setSettled({ trackingReference, state });
      }
    };
    const settleAsError = (reason: string) => {
      if (isCurrent) {
        console.error('Joinable invites could not be read', { reason });
        reportPageActionToNewRelic(JOINABLE_INVITES_READ_FAILED_PAGE_ACTION, {
          source: 'joinable-invites',
          status: reason,
        });
      }
      settle({ status: 'error' });
    };

    api.getV1trackingTrackingReferencejoinableInvites({ params: { trackingReference } }).then(
      (response) => settle({ status: 'loaded', joinedInvites: readJoinedInvites(response) }),
      (error) => {
        if (isConflict(error)) {
          settle({ status: 'single' });
        } else {
          settleAsError(failureReason(error));
        }
      },
    );

    return () => {
      isCurrent = false;
    };
  }, [trackingReference]);

  if (!trackingReference) {
    return { status: 'single' };
  }
  return settled && settled.trackingReference === trackingReference
    ? settled.state
    : { status: 'loading' };
}
